//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  ILike2BuyV2Store,
  IFindLike2BuyV2StoresResponse,
  ILike2BuyV2StoreResponse,
  IGetLike2BuyV2StoresForClientParams,
  ILike2BuyV2PreviewUrlResponse,
  IGetLike2BuyV2StoreIdsForClientParams,
  IFindLike2BuyV2StoreIdsResponse,
} from '@apis/like2buy-v2/like2BuyV2StoreModel';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function like2buyV2StoreFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'like2buy_gateway',
    `/api/v2/stores${endpoint}`,
    init,
  );
}

/**
 * Finds Like2Buy v2 stores for the current client
 *
 * GET /api/v2/stores
 */
export async function findLike2BuyV2Stores(
  req?: IGetLike2BuyV2StoresForClientParams,
  init?: RequestInit,
): Promise<ILike2BuyV2Store[] | undefined> {
  const params = makeParams(req);
  const res = await like2buyV2StoreFetch(`/?${params}`, init);
  const like2buyV2StoreResponse =
    await res.json<IFindLike2BuyV2StoresResponse>();
  return like2buyV2StoreResponse.data;
}

/**
 * Finds Like2Buy v2 storeIds for the current client
 *
 * GET /api/v2/stores/ids
 */
export async function findLike2BuyV2StoreIds(
  req?: IGetLike2BuyV2StoreIdsForClientParams,
  init?: RequestInit,
): Promise<Like2BuyV2StoreId[] | undefined> {
  const params = makeParams(req);
  const res = await like2buyV2StoreFetch(`/ids?${params}`, init);
  const like2buyV2StoreResponse =
    await res.json<IFindLike2BuyV2StoreIdsResponse>();
  return like2buyV2StoreResponse.data;
}

/**
 * Gets Like2Buy v2 store data for the given storeId
 *
 * GET /api/v2/stores/:storeId
 */
export async function getLike2BuyV2Store(
  storeId: Like2BuyV2StoreId,
  init?: RequestInit,
): Promise<ILike2BuyV2Store | undefined> {
  const res = await like2buyV2StoreFetch(`/${storeId}`, init);
  const like2buyV2StoreResponse = await res.json<ILike2BuyV2StoreResponse>();
  return like2buyV2StoreResponse.data;
}

/**
 * Gets Like2Buy v2 store preview url
 *
 * GET /api/v2/stores/:storeId/preview
 */
export async function getLike2BuyV2PreviewUrl(
  storeId: Like2BuyV2StoreId,
  init?: RequestInit,
): Promise<string> {
  const res = await like2buyV2StoreFetch(`/${storeId}/preview`, init);
  const like2buyV2StoreResponse =
    await res.json<ILike2BuyV2PreviewUrlResponse>();
  return like2buyV2StoreResponse.data;
}
